// =============================================
// ENHANCED SUMMARY REPORT WITH BUDGET TRACKING
// components/reports/SummaryReport.js
// =============================================

import React, { useMemo } from 'react'
import { Clock, DollarSign, FileText, TrendingUp, Users, Building2, Split, Target, AlertTriangle, CheckCircle, TrendingDown } from 'lucide-react'
import { processTimeEntries, calculateSummaryWithSplits } from '../../utils/timeSplitUtils'

export const SummaryReport = ({ data }) => {
  const summary = useMemo(() => {
    if (!data.length) return null
    return calculateSummaryWithSplits(data)
  }, [data])

  // Calculate budget metrics
  const budgetMetrics = useMemo(() => {
    if (!data.length) return null

    const projectsWithBudget = new Map()
    
    data.forEach(entry => {
      if (!entry.project_budget_hours) return
      
      const projectId = entry.project_id
      if (!projectsWithBudget.has(projectId)) {
        projectsWithBudget.set(projectId, {
          id: projectId,
          name: entry.project_name,
          budgetHours: entry.project_budget_hours,
          budgetAmount: entry.project_budget_amount || 0,
          actualHours: 0,
          actualAmount: 0,
          startDate: entry.project_start_date,
          endDate: entry.project_end_date
        })
      }
      
      const project = projectsWithBudget.get(projectId)
      project.actualHours += entry.duration_hours || 0
      if (entry.is_billable) {
        project.actualAmount += (entry.duration_hours || 0) * (entry.hourly_rate || 0)
      }
    })

    if (projectsWithBudget.size === 0) return null

    let healthyCount = 0
    let warningCount = 0
    let criticalCount = 0
    let totalBudgetHours = 0
    let totalActualHours = 0
    let totalBudgetAmount = 0
    let totalActualAmount = 0

    projectsWithBudget.forEach(project => {
      totalBudgetHours += project.budgetHours
      totalActualHours += project.actualHours
      totalBudgetAmount += project.budgetAmount
      totalActualAmount += project.actualAmount

      const budgetUsed = (project.actualHours / project.budgetHours) * 100
      
      // Calculate time elapsed percentage
      let timeElapsed = 50 // default
      if (project.startDate && project.endDate) {
        const now = new Date()
        const start = new Date(project.startDate)
        const end = new Date(project.endDate)
        const totalDays = Math.max(1, (end - start) / (1000 * 60 * 60 * 24))
        const elapsed = Math.max(0, (now - start) / (1000 * 60 * 60 * 24))
        timeElapsed = Math.min((elapsed / totalDays) * 100, 100)
      }

      const expectedUsed = timeElapsed
      const variance = budgetUsed - expectedUsed

      // Determine health status
      if (budgetUsed > 100 || (budgetUsed > expectedUsed && variance > 20)) {
        criticalCount++
      } else if (budgetUsed > 90 || (budgetUsed > expectedUsed && variance > 10)) {
        warningCount++
      } else {
        healthyCount++
      }
    })

    return {
      totalProjects: projectsWithBudget.size,
      healthyCount,
      warningCount,
      criticalCount,
      totalBudgetHours,
      totalActualHours,
      totalBudgetAmount,
      totalActualAmount,
      budgetUsedPercent: totalBudgetHours > 0 ? (totalActualHours / totalBudgetHours) * 100 : 0,
      amountUsedPercent: totalBudgetAmount > 0 ? (totalActualAmount / totalBudgetAmount) * 100 : 0
    }
  }, [data])

  if (!summary) {
    return (
      <div className="text-center py-12">
        <FileText className="w-12 h-12 text-slate-400 mx-auto mb-4" />
        <div className="text-slate-600">No data available for the selected filters</div>
      </div>
    )
  }

  const MetricCard = ({ icon: Icon, title, value, subtitle, color = 'blue', badge = null }) => (
    <div className="bg-white rounded-xl border border-slate-200 p-6 relative overflow-hidden">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-600">{title}</p>
          <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
          {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-lg bg-${color}-100`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
      </div>
      {badge && (
        <div className="absolute top-2 right-2">
          {badge}
        </div>
      )}
    </div>
  )

  const splitEntriesCount = data.filter(entry => entry.is_split && entry.splits?.length > 0).length

  return (
    <div className="space-y-6">
      {/* Budget Health Overview - Only show if projects have budgets */}
      {budgetMetrics && (
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Target className="w-5 h-5 text-blue-600" />
                Budget Health Overview
              </h3>
              <p className="text-sm text-slate-600 mt-1">
                {budgetMetrics.totalProjects} projects with active budgets
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="text-right">
                <div className="text-2xl font-bold text-slate-900">
                  {((budgetMetrics.healthyCount / budgetMetrics.totalProjects) * 100).toFixed(0)}%
                </div>
                <div className="text-xs text-slate-600">Health Score</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Healthy Projects */}
            <div className="bg-white rounded-lg border border-green-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-2xl font-bold text-green-600">{budgetMetrics.healthyCount}</span>
              </div>
              <div className="text-sm font-medium text-slate-900">Healthy</div>
              <div className="text-xs text-slate-600">On track</div>
            </div>

            {/* Warning Projects */}
            <div className="bg-white rounded-lg border border-yellow-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
                <span className="text-2xl font-bold text-yellow-600">{budgetMetrics.warningCount}</span>
              </div>
              <div className="text-sm font-medium text-slate-900">Warning</div>
              <div className="text-xs text-slate-600">Needs monitoring</div>
            </div>

            {/* Critical Projects */}
            <div className="bg-white rounded-lg border border-red-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <TrendingDown className="w-5 h-5 text-red-600" />
                <span className="text-2xl font-bold text-red-600">{budgetMetrics.criticalCount}</span>
              </div>
              <div className="text-sm font-medium text-slate-900">Critical</div>
              <div className="text-xs text-slate-600">At risk</div>
            </div>

            {/* Budget Usage */}
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <Clock className="w-5 h-5 text-purple-600" />
                <span className="text-2xl font-bold text-slate-900">
                  {budgetMetrics.budgetUsedPercent.toFixed(0)}%
                </span>
              </div>
              <div className="text-sm font-medium text-slate-900">Budget Used</div>
              <div className="text-xs text-slate-600">
                {budgetMetrics.totalActualHours.toFixed(0)}h / {budgetMetrics.totalBudgetHours.toFixed(0)}h
              </div>
              <div className="mt-2 w-full bg-slate-200 rounded-full h-1.5">
                <div 
                  className={`h-1.5 rounded-full transition-all ${
                    budgetMetrics.budgetUsedPercent > 100 ? 'bg-red-500' :
                    budgetMetrics.budgetUsedPercent > 90 ? 'bg-yellow-500' : 'bg-green-500'
                  }`}
                  style={{ width: `${Math.min(budgetMetrics.budgetUsedPercent, 100)}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <MetricCard
          icon={Clock}
          title="Total Hours"
          value={summary.totalHours.toFixed(1)}
          subtitle={`${summary.originalEntries} time entries`}
          color="blue"
        />
        <MetricCard
          icon={DollarSign}
          title="Revenue"
          value={`$${summary.revenue.toFixed(0)}`}
          subtitle={`${summary.billableHours.toFixed(1)} billable hours`}
          color="green"
        />
        <MetricCard
          icon={TrendingUp}
          title="Utilization Rate"
          value={`${summary.utilizationRate.toFixed(1)}%`}
          subtitle={`Avg rate: $${summary.avgRate.toFixed(0)}/hr`}
          color="purple"
        />
        <MetricCard
          icon={Users}
          title="Team Members"
          value={summary.userCount}
          subtitle="Active contributors"
          color="orange"
        />
        <MetricCard
          icon={Building2}
          title="Clients"
          value={summary.clientCount}
          subtitle="Active clients"
          color="indigo"
        />
        <MetricCard
          icon={Split}
          title="Split Entries"
          value={splitEntriesCount}
          subtitle={`${summary.splitEntries} individual splits`}
          color="pink"
        />
      </div>

      {/* Top Projects with Budget Status */}
      <TopProjectsTable data={data} />
    </div>
  )
}

const TopProjectsTable = ({ data }) => {
  const topProjects = useMemo(() => {
    const expandedEntries = processTimeEntries(data)
    const projectMap = new Map()
    
    expandedEntries.forEach(entry => {
      const key = entry.project_id
      if (!projectMap.has(key)) {
        projectMap.set(key, {
          id: key,
          name: entry.project_name,
          clientName: entry.client_name,
          hours: 0,
          revenue: 0,
          budgetHours: entry.project_budget_hours || null,
          budgetAmount: entry.project_budget_amount || null,
          startDate: entry.project_start_date,
          endDate: entry.project_end_date
        })
      }
      
      const project = projectMap.get(key)
      project.hours += entry.duration_hours || 0
      if (entry.is_billable) {
        project.revenue += (entry.duration_hours || 0) * (entry.hourly_rate || 0)
      }
    })
    
    return Array.from(projectMap.values())
      .sort((a, b) => b.hours - a.hours)
      .slice(0, 5)
  }, [data])

  const getHealthBadge = (project) => {
    if (!project.budgetHours) return null
    
    const budgetUsed = (project.hours / project.budgetHours) * 100
    
    // Calculate time elapsed percentage to match overview logic
    let timeElapsed = 50 // default assumption
    if (project.startDate && project.endDate) {
      const now = new Date()
      const start = new Date(project.startDate)
      const end = new Date(project.endDate)
      const totalDays = Math.max(1, (end - start) / (1000 * 60 * 60 * 24))
      const elapsed = Math.max(0, (now - start) / (1000 * 60 * 60 * 24))
      timeElapsed = Math.min((elapsed / totalDays) * 100, 100)
    }

    const variance = budgetUsed - timeElapsed
    
    // Use EXACT same logic as budgetMetrics calculation
    if (budgetUsed > 100 || (budgetUsed > timeElapsed && variance > 20)) {
      return <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">Critical</span>
    } else if (budgetUsed > 90 || (budgetUsed > timeElapsed && variance > 10)) {
      return <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 rounded-full">Warning</span>
    } else {
      return <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">Healthy</span>
    }
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200">
      <div className="p-6 border-b border-slate-200">
        <h3 className="text-lg font-semibold text-slate-900">Top 5 Projects by Hours</h3>
        <p className="text-sm text-slate-600 mt-1">Including time split calculations and budget status</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Project</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Client</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Hours</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Cost</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {topProjects.map((project) => (
              <tr key={project.id} className="hover:bg-slate-50">
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-slate-900">{project.name}</div>
                  {project.budgetHours && (
                    <div className="text-xs text-slate-500 mt-0.5">
                      Budget: {project.budgetHours}h
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-slate-600">{project.clientName}</td>
                <td className="px-6 py-4">
                  <div className="text-sm text-slate-900">{project.hours.toFixed(1)}h</div>
                  {project.budgetHours && (
                    <div className="text-xs text-slate-500">
                      {((project.hours / project.budgetHours) * 100).toFixed(0)}% used
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 text-sm font-medium text-green-600">
                  ${project.revenue.toFixed(0)}
                </td>
                <td className="px-6 py-4">
                  {getHealthBadge(project) || <span className="text-xs text-slate-400">No budget</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default SummaryReport